import { useThemeStore } from '@/store';

/**
 * Chart color tokens for this app. Recharts (already a dependency -- see
 * `package.json`) is the lightest option that satisfies the `dataviz` skill's
 * guidance without adding a new library, so this module supplies the
 * skill's validated default palette (`references/palette.md`) as plain hex,
 * split light/dark, for recharts' inline `fill`/`stroke` props to consume.
 *
 * This is the first charting code in the app, so it is the precedent Tasks
 * 20/21 build their charts against -- new charts should reuse these tokens
 * and the mode hook below rather than hand-picking colors.
 *
 * Categorical slots 1 (blue) and 2 (orange) are validated together via
 * `scripts/validate_palette.js "<slot1>,<slot2>" --mode light|dark` in both
 * modes: worst adjacent CVD deltaE 24.7 light / 26.8 dark (>= 8 target),
 * worst normal-vision deltaE 33.6 light / 31.8 dark (>= 15 floor). Only two
 * slots are used here (Leads vs Converted, in the Sales-over-time chart) --
 * well inside the "1-3 series: color alone is comfortable" band, so no
 * additional texture/pattern channel is needed.
 */
export interface ChartColors {
  surface: string;
  ink: { primary: string; secondary: string; muted: string };
  grid: string;
  axis: string;
  /** Single hue for magnitude (sequential) charts -- one flat fill, no legend. */
  sequential: string;
  /** Fixed-order categorical slots. Assign by series identity, never by rank. */
  categorical: [string, string];
}

const LIGHT: ChartColors = {
  surface: '#fcfcfb',
  ink: { primary: '#0b0b0b', secondary: '#52514e', muted: '#898781' },
  grid: '#e1e0d9',
  axis: '#c3c2b7',
  sequential: '#2a78d6',
  categorical: ['#2a78d6', '#eb6834'],
};

const DARK: ChartColors = {
  surface: '#1a1a19',
  ink: { primary: '#ffffff', secondary: '#c3c2b7', muted: '#898781' },
  grid: '#2c2c2a',
  axis: '#383835',
  sequential: '#3987e5',
  categorical: ['#3987e5', '#d95926'],
};

/** Resolves the chart palette for the viewer's current theme (light/dark),
 * reactively -- re-renders when `useThemeStore`'s resolvedTheme flips. */
export function useChartColors(): ChartColors {
  const resolvedTheme = useThemeStore((s) => s.resolvedTheme);
  return resolvedTheme === 'dark' ? DARK : LIGHT;
}
